import { motion } from "framer-motion";
import { ArrowRight, Building2, Check, Compass, FileCheck, MapPin, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "@/utils/translations";
import { localizePath } from "@/utils/languageRoutes";
import type { ServiceContent } from "@/types/service";
import { SectionHeading, EASE } from "./demo-shared";

interface DemoServicesProps {
  language: string;
}

const serviceConfig = [
  { key: "strategy", Icon: Compass, badge: { EN: "Feasibility Study", KO: "시장 조사" } },
  { key: "incorporation", Icon: Building2, badge: { EN: "Incorporation", KO: "법인 설립" } },
  { key: "recruitment", Icon: Users, badge: { EN: "Recruitment", KO: "인력 채용" } },
  { key: "taxation", Icon: FileCheck, badge: { EN: "Compliance", KO: "세무·회계" } },
  { key: "realEstate", Icon: MapPin, badge: { EN: "Real Estate", KO: "부동산" } },
] as const;

const DemoServices = ({ language }: DemoServicesProps) => {
  const t = useTranslation(language);
  const isKo = language === "KO";

  const getContent = (key: string): ServiceContent => t.services[key as keyof typeof t.services] as ServiceContent;

  return (
    <section className="relative bg-white py-24 md:py-32" id="services">
      <div
        aria-hidden
        className="absolute left-1/2 top-0 h-px w-[min(90%,72rem)] -translate-x-1/2 bg-gradient-to-r from-transparent via-slate-200 to-transparent"
      />
      <div className="container mx-auto max-w-6xl px-4">
        <SectionHeading
          no="03"
          eyebrow={isKo ? "서비스" : "Services"}
          title={t.services.title}
          sub={t.services.subtitle}
        />

        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {serviceConfig.map((service, index) => {
            const content = getContent(service.key);
            const Icon = service.Icon;
            return (
              <motion.article
                key={service.key}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (index % 3) * 0.08, ease: [...EASE] }}
                className="group relative overflow-hidden rounded-3xl border border-slate-200/80 bg-[#FBFBF9] p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-primary/25 hover:bg-white hover:shadow-[0_20px_50px_-20px_rgb(20,52,164,0.25)]"
              >
                {/* watermark index */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute -right-2 -top-5 select-none font-mono text-[5.5rem] font-bold leading-none text-slate-900/[0.04] transition-colors duration-300 group-hover:text-primary/[0.06]"
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-[3px] origin-left scale-x-0 bg-gradient-to-r from-primary to-secondary transition-transform duration-500 group-hover:scale-x-100"
                />

                <div className="flex items-center justify-between">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-primary shadow-sm ring-1 ring-slate-200/80 transition-all duration-300 group-hover:bg-primary group-hover:text-white group-hover:shadow-primary/30">
                    <Icon size={22} strokeWidth={2} />
                  </span>
                  <span className="rounded-full bg-primary/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
                    {isKo ? service.badge.KO : service.badge.EN}
                  </span>
                </div>

                <h3 className="mt-5 text-lg font-bold leading-snug text-slate-900">{content.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{content.description}</p>

                <ul className="mt-5 space-y-2">
                  {content.items.slice(0, 4).map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[13px] leading-snug text-slate-500">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-secondary" strokeWidth={3} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.article>
            );
          })}

          {/* CTA tile */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: 0.16, ease: [...EASE] }}
            className="group relative flex flex-col justify-between overflow-hidden rounded-3xl bg-primary p-7 text-white shadow-xl shadow-primary/25"
          >
            {/* corridor echo */}
            <svg aria-hidden viewBox="0 0 300 300" fill="none" className="absolute inset-0 h-full w-full opacity-20">
              <path
                d="M 250 40 Q 240 170 60 250"
                stroke="white"
                strokeWidth="2"
                strokeDasharray="6 10"
                strokeLinecap="round"
                style={{ animation: "demo-dash 2.4s linear infinite" }}
              />
              <circle cx="250" cy="40" r="4" fill="#FF9F1C" />
              <circle cx="60" cy="250" r="4" fill="#FF9F1C" />
            </svg>
            <div className="relative">
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/60">
                {isKo ? "시작할 준비가 되셨나요?" : "Ready to start?"}
              </p>
              <h3 className="mt-3 whitespace-pre-line text-2xl font-bold leading-snug">
                {isKo ? "인도 진출의 첫 걸음을\n함께 하세요" : "Take the first step\ninto India with us"}
              </h3>
            </div>
            <Link to={localizePath("/contact", language)} className="relative mt-8 inline-block">
              <span className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-primary transition-all group-hover:gap-3 group-hover:shadow-lg">
                {isKo ? "무료 상담 신청" : "Book a free consultation"}
                <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DemoServices;
